import { gql, useLazyQuery } from "@apollo/client";
import React, { useEffect, useState, useContext } from "react";
import { convertToCSV } from "@solsys/solsys-common";
import {
  OptionsMenu,
  DateTimeInput,
  Button,
  NotificationContext,
  ResponsiveTableView,
  Tabs,
} from "@solsys/solsys-reactjs";
import { DateTime } from "luxon";
import { FaPlus, FaCog, FaFileCsv } from "react-icons/fa";
import StatusDropDown from "../../dropdowns/StatusDropDown";
import TagDropDown from "../../dropdowns/TagDropDown";
import ListView from "../mapview/ListView";
import { withTimestampSortOptions, resetListSort } from "../../listSort";

const gqlQuery = gql`
  query prospectList($prospect_list_request: prospect_list_request,
  $tag2Prospect_list_request: tag2Prospect_list_request) {
    prospect(prospect_list_request: $prospect_list_request) {
      edges {
        id
        name
        status {
          name
        }
        status_id
      }
      totalCount
    }
    tag2Prospect(tag2Prospect_list_request: $tag2Prospect_list_request) {
      edges {
        id
        prospect_id
      }
      totalCount
    }
  }
`;

export const ListOptions = {
  sortBy: "sortBy",
  sortDirection: "sortDirection",
  name: "name",
  statusId: "status_id",
  tab: "tab",
  tagIds: "tag_ids",
  startDate: 'start_date',
  endDate: 'end_date',
  search: "search",
};

export default function ProspectList({ id: entityId, action, ...props }) {
  const { addNotification } = useContext(NotificationContext);
  const isAdd = action === "add";

  const [requestState, setRequestState] = props.state;
  let { sortBy, sortDirection, name, statusId, tab, startDate, endDate, tagIds, search } = requestState;
  const setListOptions = (state) =>
    setRequestState({ ...requestState, ...state });
  const [optionSortBy, setOptionSortBy] = useState(sortBy);
  const [optionSortDirection, setOptionSortDirection] = useState(sortDirection);
  const [optionName, setOptionName] = useState(name);
  const [optionStatusId, setOptionStatusId] = useState(statusId);
  const [optionTab, setOptionTab] = useState(tab);
  const [activeTab, setActiveTab] = useState('Data');
  const [optionTagIds, setOptionTagIds] = useState(tagIds ? tagIds : []);
  const [optionStartDate, setOptionStartDate] = useState(null);
  const [optionEndDate, setOptionEndDate] = useState(null);
  const applyOptions = () => {
    setListOptions({
      sortBy: optionSortBy,
      sortDirection: optionSortDirection,
      name: optionName,
      statusId: optionStatusId,
      tab: optionTab,
      tagIds: optionTagIds.length > 0 ? optionTagIds : null,
      startDate: optionStartDate,
      endDate: optionEndDate,
    });
  };
  const resetOptions = () => {
    const defaults = resetListSort();
    setOptionSortDirection(defaults.sortDirection);
    setOptionSortBy(defaults.sortBy);
    setOptionName("");
    setOptionStatusId(null);
    setOptionTab("Chat");
    setOptionTagIds([]);
    setOptionStartDate(null);
    setOptionEndDate(null);
  };

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [limit, setLimit] = useState(100);

  const sortByOptions = withTimestampSortOptions([
    { text: "Prospect ID", sortBy: "id" },
    { text: "Name", sortBy: "name" },
    { text: "Status", sortBy: "status_id" },
  ]);
  const sortDirectionOptions = [
    {
      text: "A to Z",
      sortDirection: "ASC",
    },
    {
      text: "Z to A",
      sortDirection: "DESC",
    },
  ];

  let variables = {
    prospect_list_request: {
      filters: [],
      pagination: { limit },
      sorting: [],
    },
    tag2Prospect_list_request: {
      filters: [{
        field: "tag_id",
        operator: "in",
        value: `(${tagIds ? tagIds.join(", ") : ''})`,
      }]
    },
  };
  if (sortBy && sortDirection) {
    let sortOption = sortByOptions.find((v) => v.sortBy === sortBy);
    variables.prospect_list_request.sorting.push({
      field: sortBy,
      direction: sortDirection,
      entity: sortOption?.sortEntity,
    });
  }
  if (name)
    variables.prospect_list_request.filters.push({
      field: "name",
      operator: "like",
      value: `%${name}%`,
    });
  if (search?.trim())
    variables.prospect_list_request.filters.push({
      field: "search",
      value: search.trim(),
    });
  if (statusId)
    variables.prospect_list_request.filters.push({
      field: "status_id",
      operator: "=",
      value: statusId.toString(),
    });
  if(startDate || endDate) {
    if(startDate && endDate) {
      variables.prospect_list_request.filters.push({
        field: "created_at",
        operator: "between",
        value: `${startDate.toString()},${endDate.toString()}`,
      });
    } else if(startDate) {
      variables.prospect_list_request.filters.push({
        field: "created_at",
        operator: ">",
        value: startDate.toString(),
      });
    } else if(endDate) {
      variables.prospect_list_request.filters.push({
        field: "created_at",
        operator: "<",
        value: endDate.toString(),
      });
    }
  }
  const [csvQuery, { loading: csvLoading }] = useLazyQuery(gqlQuery);
  const [list, setList] = useState([]);
  const [queryData, setQueryData] = useState(null);
  const [refetch, { loading, data }] = useLazyQuery(gqlQuery, {
    variables,
    pollInterval: 5000,
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
  });

  useEffect(() => {
    if (data) setQueryData(data);
  }, [data]);
  
  const selectItem = (id) => {
    props.onSelect(id, true);
  }

  const setStartDate = (date) => {
    if(!date) setOptionStartDate(null);
    else setOptionStartDate(date.toString() === 'Invalid DateTime' ? null : date.toString());
  }

  const setEndDate = (date) => {
    if(!date) setOptionEndDate(null);
    else {
      if (date.toString() === 'Invalid DateTime') {
        setOptionEndDate(null);
      } else {
        const newDate = date.plus({ days: 1, seconds: -1 });
        setOptionEndDate(newDate.toString());
      }
    }
  }

  const count = data?.prospect?.totalCount ?? 0;
  if (props.isMaster && props.autoSelect && !loading && !isAdd && !entityId) {
    // props.onSelect(0);
    props.onSelect(list.length ? list[0].id : 0);
  }

  useEffect(() => {
    if (props.onStateChange) props.onStateChange(requestState);
    refetch({ variables });
  }, [requestState, limit]);

  useEffect(() => {
    if (!queryData) return;

    const prospects = queryData?.prospect?.edges ?? [];
    const tag2Prospects = queryData?.tag2Prospect?.edges ?? [];
    const prospect_ids = tag2Prospects.map(item => item.prospect_id);
    setList(prospects.filter((prospect) => prospect_ids.includes(prospect.id) || !tagIds));
  }, [queryData, tagIds]);


  return (
    <OptionsMenu isOpen={isFilterMenuOpen}>
      <OptionsMenu.Menu>
        <div className="options-tools">
          <Button
            onClick={() => {
              applyOptions();
              setIsFilterMenuOpen(false);
            }}
            loading={loading}
          >
            Apply
          </Button>
          <button onClick={resetOptions}>Reset</button>
          <button onClick={() => setIsFilterMenuOpen(false)}>Close</button>
        </div>

        <div className="options-section">
          <h3>Filters</h3>

          <div className="option">
            <label>Prospect Name:</label>
            <input
              value={optionName}
              onChange={(e) => setOptionName(e.target.value)}
            />
          </div>

          <div className="option">
            <label>Prospect Status:</label>
            <StatusDropDown
              selected={optionStatusId}
              onChange={(id) => setOptionStatusId(id)}
            />
          </div>
          <div className="option">
            <label>Tags:</label>
            <TagDropDown
              selected={optionTagIds}
              onSelect={setOptionTagIds}
              searchEnabled
              multiple={true}
            />
          </div>
          <div className="option">
            <label>From:</label>
            <DateTimeInput value={DateTime.fromISO(optionStartDate)} timeZone={"UTC"} onChange={(date) => setStartDate(date)} dateOnly={true}/>
          </div>
          <div className="option">
            <label>To:</label>
            <DateTimeInput value={DateTime.fromISO(optionEndDate)} timeZone={"UTC"} onChange={(date) => setEndDate(date)} dateOnly={true}/>
          </div>
        </div>

        <div className="options-section">
          <h3>Sorting</h3>

          <div className="option">
            <label>Sort By:</label>
            <div>
              <select
                value={optionSortBy}
                onChange={(event) => setOptionSortBy(event.target.value)}
              >
                {sortByOptions.map((option) => (
                  <option key={option.text} value={option.sortBy}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="option">
            <label>Sort Order:</label>
            <div>
              <select
                value={optionSortDirection}
                onChange={(event) => setOptionSortDirection(event.target.value)}
              >
                {sortDirectionOptions.map((option) => (
                  <option key={option.text} value={option.sortDirection}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="options-tools">
          <Button
            onClick={() => {
              applyOptions();
              setIsFilterMenuOpen(false);
            }}
            loading={loading}
          >
            Apply
          </Button>
          <button onClick={resetOptions}>Reset</button>
          <button onClick={() => setIsFilterMenuOpen(false)}>Close</button>
        </div>
      </OptionsMenu.Menu>
      <OptionsMenu.Layout>
        <div className="page-header">
          <span className="page-header-title">Prospects</span>
          <div className="page-header-options">
            <Button
              onClick={() => {
                variables.prospect_list_request.pagination = { limit: 10000 };
                csvQuery({ variables }).then((response) => {
                  const csvData = convertToCSV(
                    response?.data?.prospect?.edges.map((entity) => ({
                      ID: entity.id,
                      Name: entity.name,
                      Status: entity.status?.name,
                    }))
                  );
                  const blob = new Blob([csvData], {
                    type: "text/csv;charset=utf-8;",
                  });
                  const url = URL.createObjectURL(blob);

                  const link = document.createElement("a");
                  link.href = url;
                  link.setAttribute(
                    "download",
                    `prospects_${DateTime.now()
                      .toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
                      .replace(/:/g, "-")
                      .replace(/, /g, "_")}.csv`
                  );
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                });
              }}
              loading={csvLoading}
            >
              <FaFileCsv />
            </Button>
            {props.isMaster && (
              <button onClick={() => props.onSelect(0)}>
                <FaPlus />
              </button>
            )}
            <button onClick={() => setIsFilterMenuOpen(true)}>
              <FaCog />
            </button>
          </div>
        </div>

        <Tabs autoSelect state={[activeTab, setActiveTab]}>
          <div label='Data'>
            <input
              value={ search ?? "" }
              style={ { width: "100%" } }
              placeholder="Search ID or name..."
              type="text"
              onChange={ (e) => {
                setListOptions({ search: e.target.value });
              } }
            />
            <ResponsiveTableView
              style={{ height: "100%" }}
              loading={loading}
              headers={[
                {
                  label: "ID",
                },
                {
                  label: "Name",
                },
                {
                  label: "Status",
                },
              ]}
              data={list.map((entity) => ({
                id: entity.id,
                ID: entity.id,
                Name: entity.name,
                Status: entity.status?.name,
                selected: String(entity.id) === String(entityId) && !isAdd,
              }))}
              identityColumnKey="ID"
              onRowClick={(row) => {
                props.onSelect(row.id, true);
              }}
              totalCount={count}
              addMoreResults={() => {
                setLimit(limit + 100);
              }}
            />
          </div>
          <div label='Map'>
            <ListView type="prospect" style={{ height: "calc(100vh - 86px)" }} onSelect={selectItem}/>
          </div>
        </Tabs>
      </OptionsMenu.Layout>
    </OptionsMenu>
  );
}
